import React, { useState } from "react";
import { Container } from "./style";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setChallengeData } from "../../../store/features/CreateChallenge";
import {
  Button,
  Form,
  Card,
  Empty,
  Spin,
  Avatar,
  Input,
  InputNumber,
  DatePicker,
  message,
  Progress,
} from "antd";
import { LeftOutlined, CopyOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { MAX_CHALLENGE_DAYS } from "../../../utils/const";
// import PutUpload from "../../../components/PutUpload";

const { TextArea } = Input;

export default function CreatePrompt() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [dayList, setDayList] = useState([]);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  // const [uploadedFileInfo, setUploadedFileInfo] = useState(null);

  // Get token from Redux store
  const loginInfo = useSelector((state) => state.userInfo.loginInfo);
  const token = loginInfo?.idToken || "";

  // Form
  const [form] = Form.useForm();
  const numberOfDaysValue = Form.useWatch("numberOfDays", form);
  const isOverMaxDays = numberOfDaysValue > MAX_CHALLENGE_DAYS;

  // Handle file upload success
  // const handleFileUpload = (fileUrl, fileInfo) => {
  //   setUploadedFileUrl(fileUrl);
  //   setUploadedFileInfo(fileInfo);
  //   console.log("File uploaded successfully:", fileUrl, fileInfo);
  // };

  const [aiChallenge, setAiChallenge] = useState(null);

  // Stream generation function
  const generateChallengeStream = async (payload) => {
    try {
      const baseUrl = process.env.REACT_APP_CUSTOM_ENV === 'dev'
        ? 'https://dev.uvita-coach.link'
        : 'https://prod.uvita-coach.link';

      const apiUrl = `${baseUrl}/api/challenge/generate-stream`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // Track days being built
      const daysMap = new Map();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const event = JSON.parse(line.slice(6));
              handleStreamEvent(event, daysMap);
            } catch (e) {
              console.error("Failed to parse event:", e);
            }
          }
        }
      }
    } catch (error) {
      console.error("Stream error:", error);
      throw error;
    }
  };

  // Handle stream events
  const handleStreamEvent = (event, daysMap) => {
    console.log("Stream event:", event);

    switch (event.type) {
      case "init":
      case "challenge_created":
        setProgressMessage(event.message || "Initializing...");
        setProgress(event.progress || 5);
        if (event.challengeId) {
          setAiChallenge({ _id: event.challengeId });
        }
        break;

      case "ai_generating":
      case "ai_progress":
        setProgressMessage(event.message || "AI generating content...");
        setProgress(event.progress || 25);
        break;

      case "ai_complete":
        setProgressMessage("AI generation complete, saving to database...");
        setProgress(event.progress || 50);
        break;

      case "saving_days":
        setProgressMessage("Saving challenge days...");
        setProgress(event.progress || 55);
        break;

      case "day_item":
        // Add item to day
        const dayNum = event.dayNumber;
        if (!daysMap.has(dayNum)) {
          daysMap.set(dayNum, {
            dayNumber: dayNum,
            order: dayNum,
            prompt: null,
            insight: null,
            action: null,
          });
        }

        const day = daysMap.get(dayNum);
        if (event.itemType === "prompt") {
          day.prompt = event.item;
        } else if (event.itemType === "insight") {
          day.insight = event.item;
        } else if (event.itemType === "action") {
          day.action = event.item;
        }

        // Update display in real-time
        setDayList(
          Array.from(daysMap.values()).sort((a, b) => a.dayNumber - b.dayNumber)
        );
        setProgress(event.progress || 60);
        setProgressMessage(`Creating Day ${dayNum}/${event.totalDays}...`);
        break;

      case "day_complete":
        setProgress(event.progress || 70);
        setProgressMessage(`Day ${event.dayNumber} complete`);
        break;

      case "complete":
        setProgress(100);
        setProgressMessage("Challenge generated successfully!");
        if (event.challengeId) {
          setAiChallenge((prev) => ({
            ...prev,
            _id: event.challengeId,
            ...event.summary,
          }));
        }
        message.success("Challenge generated successfully!");
        setLoading(false);
        setIsStreaming(false);
        break;

      case "error":
        message.error(event.message || "Failed to generate challenge");
        setLoading(false);
        setIsStreaming(false);
        break;

      default:
        console.log("Unknown event type:", event.type);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    setIsStreaming(true);
    setProgress(0);
    setProgressMessage("Starting generation...");
    setDayList([]);
    setAiChallenge(null);

    try {
      const numberOfDays = values.numberOfDays;
      const scheduledAt =
        values.scheduledAt.format("YYYY-MM-DDTHH:mm:ss.000") + "Z";
      const description = values.description;

      const payload = {
        title: `${numberOfDays}-Day Personal Development Challenge`,
        description: description,
        numberOfDays: numberOfDays,
        //         aiGenerationPrompt: `Create a ${numberOfDays}-day personal development challenge starting from ${scheduledAt}.

        // Description: "${description}"
        // ${
        //   uploadedFileUrl
        //     ? "Uploaded file content should be analyzed and incorporated into the challenge."
        //     : "No file uploaded - use general knowledge about the topic."
        // }

        // For each day, provide exactly three components:
        // 1. REFLECTION: A thought-provoking journaling question (1-2 sentences)
        // 2. INSIGHT: Motivational text or wisdom (2-3 sentences)
        // 3. ACTION: A practical step or exercise (1-2 sentences)

        // Format the response as structured data that can be parsed into the challenge system. Each day should build upon the previous day's content.`,
        scheduledAt: scheduledAt,
        uploadedFileUrl: uploadedFileUrl,
        visibility: "PRIVATE",
        status: "DRAFTED",
      };

      console.log("Submit data:", payload);
      await generateChallengeStream(payload);
    } catch (error) {
      console.error("Failed to generate challenge content:", error);
      message.error("Failed to generate challenge content, please try again");
      setLoading(false);
      setIsStreaming(false);
      setProgress(0);
      setProgressMessage("");
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Form validation failed:", errorInfo);
    setLoading(false);
  };

  const onClear = () => {
    form.resetFields();
    setUploadedFileUrl(null);
    // setUploadedFileInfo(null);
    setDayList([]);
    setProgress(0);
    setProgressMessage("");
    setAiChallenge(null);
    message.info("Form cleared");
  };

  const onBack = () => {
    navigate("/contentManager?tabName=4");
  };

  const onEdit = () => {
    if (dayList.length === 0) {
      message.warning("No challenge content to edit");
      return;
    }

    // 构建表单数据
    const paramData = Object.assign(aiChallenge, {
      days: dayList,
      numberOfDays: form.getFieldValue("numberOfDays"),
      scheduledAt: form.getFieldValue("scheduledAt"),
      visibility: "PRIVATE",
      status: "DRAFTED",
    });

    // 将数据存储到Redux store
    dispatch(setChallengeData(paramData));

    // 跳转到EditChallenge页面
    navigate("/EditChallenge");
  };

  const onCopy = async () => {
    if (dayList.length === 0) {
      message.warning("No content to copy");
      return;
    }

    // 格式化内容，和展示的格式保持一致
    const formattedContent = dayList
      .map((dayItem) => {
        const dayNumber = dayItem.dayNumber || dayItem.order || 1;
        const reflection = dayItem.prompt?.content || "No reflection content";
        const insight = dayItem.insight?.content || "No insight content";
        const action = dayItem.action?.content || "No action content";

        return `Day ${dayNumber}

📝 Reflection
${reflection}

💡 Insight
${insight}

🎯 Action
${action}`;
      })
      .join("\n\n" + "=".repeat(50) + "\n\n");

    try {
      await navigator.clipboard.writeText(formattedContent);
      message.success("Content is copied");
    } catch (error) {
      console.error("Failed to copy content:", error);
      // 降级方案：使用传统方法
      const textArea = document.createElement("textarea");
      textArea.value = formattedContent;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        message.success("Content is copied");
      } catch (err) {
        message.error("Failed to copy content");
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <Container forceRender>
      <div className="page-header">
        <Button
          className="btn-back"
          icon={<LeftOutlined />}
          type="text"
          onClick={onBack}
        >
          Content Manager
        </Button>
        <div className="page-title">AI copilot - challenge generation</div>
      </div>
      <div className="page-form">
        <div className="left-side">
          <Card title="Customize guidance" className="full-height">
            <Form
              form={form}
              name="aiForm"
              layout="vertical"
              size="large"
              className="full-width"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
            >
              <Form.Item
                label="Duration"
                name="numberOfDays"
                rules={[
                  {
                    required: true,
                    message: "Please input the number of days",
                  },
                  {
                    type: "number",
                    max: MAX_CHALLENGE_DAYS,
                    message: `Maximum ${MAX_CHALLENGE_DAYS} days`,
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  step={1}
                  precision={0}
                  placeholder="Enter the number of days"
                  style={{ width: "100%" }}
                  parser={(value) => {
                    const parsed = parseInt(value?.replace(/\D/g, ""), 10);
                    return isNaN(parsed) || parsed < 1 ? "" : parsed;
                  }}
                  formatter={(value) => {
                    if (!value) return "";
                    const num = parseInt(value, 10);
                    return isNaN(num) ? "" : String(num);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Start Time"
                name="scheduledAt"
                rules={[
                  { required: true, message: "Please select a start time" },
                ]}
                initialValue={dayjs()}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  showTime={{ format: "HH:mm" }}
                  format="MM/DD/YYYY HH:mm"
                  disabledDate={(current) =>
                    current && current < new Date().setHours(0, 0, 0, 0)
                  }
                  placeholder="Select a start time"
                />
              </Form.Item>
              <Form.Item
                label="Guidance"
                name="description"
                rules={[{ required: true, message: "Please input guidance" }]}
              >
                <TextArea
                  placeholder="Add a short brief (e.g., audience, focus area, goals) to guide content customization.
Example: Create a challenge to help a group of designers working on innovative projects experience the emotion of awe and improve daily well-being."
                  autoSize={{ minRows: 10, maxRows: 16 }}
                  maxLength={30000}
                  showCount
                />
              </Form.Item>
              {/* <Form.Item
                label="File Upload (Optional)"
                help="Upload a file to add context for content generation. Supports Text, Word, and PDF (max 10 MB)."
              >
                <PutUpload
                  fileName="challenge_file"
                  onChange={handleFileUpload}
                  accept="*/
              /*"
                  maxSize={10 * 1024 * 1024}
                  showUploadList={true}
                  listType="text"
                  autoGenerateUrl={false}
                />
                {uploadedFileInfo && (
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    Uploaded: {uploadedFileInfo.name} (
                    {(uploadedFileInfo.size / 1024 / 1024).toFixed(2)}MB)
                  </div>
                )}
              </Form.Item> */}
              <br />
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={isStreaming || isOverMaxDays}
                >
                  {isStreaming ? "Generating..." : "Submit"}
                </Button>
                <Button
                  className="ml-10"
                  loading={loading}
                  onClick={onClear}
                  disabled={isStreaming}
                >
                  Clear
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
        <div className="right-side">
          <Spin spinning={loading}>
            <Card
              title={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Generated challenge content</span>
                  {dayList.length > 0 && (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={onCopy}
                      >
                        Copy
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={onEdit}
                      >
                        Edit
                      </Button>
                    </div>
                  )}
                </div>
              }
              bodyStyle={{ minHeight: "calc(100vh - 267px)" }}
            >
              {/* Progress bar */}
              {isStreaming && (
                <div
                  style={{
                    marginBottom: "24px",
                    padding: "16px",
                    backgroundColor: "#f0f5ff",
                    border: "1px solid #adc6ff",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Spin size="small" />
                    <span style={{ fontSize: "14px", color: "#1890ff" }}>
                      {progressMessage}
                    </span>
                  </div>
                  <Progress
                    percent={progress}
                    status={progress === 100 ? "success" : "active"}
                    strokeColor={{
                      "0%": "#108ee9",
                      "100%": "#87d068",
                    }}
                  />
                </div>
              )}

              {dayList && dayList.length < 1 && !isStreaming && (
                <Empty
                  className="empty-box"
                  description="Fill out the form and click Submit to generate AI-assisted content."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
              {dayList.length > 0 && (
                <div style={{ padding: "16px 0" }}>
                  {!isStreaming && (
                    <div
                      style={{
                        marginBottom: "16px",
                        padding: "8px 12px",
                        backgroundColor: "#f6ffed",
                        border: "1px solid #b7eb8f",
                        borderRadius: "6px",
                        fontSize: "14px",
                        color: "#52c41a",
                      }}
                    >
                      ✅ Successfully generated {dayList.length} days of
                      challenge content
                    </div>
                  )}
                  {dayList.map((dayItem, index) => {
                    const isComplete =
                      dayItem.prompt && dayItem.insight && dayItem.action;
                    const isGenerating = isStreaming && !isComplete;

                    return (
                      <div
                        key={index}
                        style={{
                          marginBottom: "24px",
                          padding: "16px",
                          border: `1px solid ${isGenerating ? "#1890ff" : "#f0f0f0"
                            }`,
                          borderRadius: "8px",
                          backgroundColor: "#fafafa",
                          animation: "fadeIn 0.5s ease-in",
                          opacity: isGenerating ? 0.8 : 1,
                          transition: "all 0.3s ease",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "16px",
                          }}
                        >
                          <Avatar
                            src={`https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/ChatGPT_logo.svg/1200px-ChatGPT_logo.svg.png`}
                            size="small"
                          />
                          <span
                            style={{ fontWeight: "bold", fontSize: "16px" }}
                          >
                            Day {dayItem.dayNumber}
                          </span>
                          {isGenerating && (
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#1890ff",
                                marginLeft: "8px",
                              }}
                            >
                              <Spin size="small" /> Generating...
                            </span>
                          )}
                        </div>

                        {/* 显示每天的三条内容 */}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                          }}
                        >
                          {/* Prompt (Journaling Question) */}
                          <div
                            style={{
                              padding: "12px",
                              backgroundColor: "#fff",
                              borderRadius: "6px",
                              border: "1px solid #e8e8e8",
                              lineHeight: "1.6",
                              fontSize: "14px",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#1890ff",
                                marginBottom: "6px",
                                fontSize: "12px",
                              }}
                            >
                              📝 Reflection
                            </div>
                            <div style={{ color: "#666" }}>
                              {dayItem.prompt?.content ||
                                "No reflection content"}
                            </div>
                          </div>

                          {/* Insight (Motivational Text) */}
                          <div
                            style={{
                              padding: "12px",
                              backgroundColor: "#fff",
                              borderRadius: "6px",
                              border: "1px solid #e8e8e8",
                              lineHeight: "1.6",
                              fontSize: "14px",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#1890ff",
                                marginBottom: "6px",
                                fontSize: "12px",
                              }}
                            >
                              💡 Insight
                            </div>
                            <div style={{ color: "#666" }}>
                              {dayItem.insight?.content || "No insight content"}
                            </div>
                          </div>

                          {/* Action (Practical Step) */}
                          <div
                            style={{
                              padding: "12px",
                              backgroundColor: "#fff",
                              borderRadius: "6px",
                              border: "1px solid #e8e8e8",
                              lineHeight: "1.6",
                              fontSize: "14px",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#1890ff",
                                marginBottom: "6px",
                                fontSize: "12px",
                              }}
                            >
                              🎯 Action
                            </div>
                            <div style={{ color: "#666" }}>
                              {dayItem.action?.content || "No action content"}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </Spin>
        </div>
      </div>
    </Container>
  );
}
